import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { FC } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';

import { BarChart, Histogram, PieChart, TreemapChart } from '../components';
import { NoData } from '../components/no-data';
import { ProgressBarChart } from '../components/progress-bar';
import { WidgetNivo } from '../components/widget-nivo';
import { CC_CHART_LEGENDS } from '../consts';
import { useDashboardStore } from '../dashboard-store';
import { CodeDashboard as CodeDashboardType, NivoCodeDashboardType } from '../types';
import {
  calculateCategoryPercentage,
  convertBackendDataToNivo,
  getPieTotalValue,
  shortenNumber,
} from '../utils';

const ResponsiveGridLayout = WidthProvider(Responsive);

const breakpoints = { xxl: 1536, xl: 1280, lg: 1024, md: 768 };

const cols = { xxl: 12, xl: 12, lg: 12, md: 12 };

interface Props {
  data: CodeDashboardType;
}

export const CodeDashboard: FC<Props> = ({ data }) => {
  const isResizable = useDashboardStore((state) => state.isResizable);

  const nivoData = convertBackendDataToNivo(
    data,
  ) as unknown as NivoCodeDashboardType;

  const {
    codeUniqueness,
    customFormatsUsed,
    cyclomaticComplexity,
    dataStepsByCategory,
    libraryEngines,
    linesOfCode,
    programBySizeInTokens,
    programsByWorkloadTypes,
    programUniqueness,
    SQLByCategory,
    sqlPassThroughShare,
    statementsByCategory,
    tokensByWorkloadTypes,
    tokensUnsupported,
    top5BuiltinFormats,
    top5fileNameDevices,
    top5fileNameExtensions,
    top5Functions,
    top5Procs,
    topMacros,
    totalPrograms,
    totalTokens,
    uniqueMacros,
  } = nivoData ?? {};

  const totalDataSteps = shortenNumber(
    getPieTotalValue(dataStepsByCategory),
    0,
  );
  const totalStatements = shortenNumber(
    getPieTotalValue(statementsByCategory),
    0,
  );
  const totalSQL = shortenNumber(getPieTotalValue(SQLByCategory), 0);

  const sqlPassThroughPercentage = calculateCategoryPercentage(
    sqlPassThroughShare,
    'pass',
    'regular',
  );

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {!data ? (
        <NoData />
      ) : (
        <>
          <div className="flex  w-full flex-col items-start">
            <h1 className=" py-4 pt-2 text-3xl font-medium text-slate-300">
              Code Analytics
            </h1>
            <ResponsiveGridLayout
              className="w-full "
              breakpoints={breakpoints}
              cols={cols}
            >
              <div
                className="grid-cell"
                key="0"
                data-grid={{
                  w: 2,
                  h: 1,
                  x: 0,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Lines of Code"
                  secondaryText={shortenNumber(linesOfCode, 2)}
                  classNames={{
                    root: 'h-full ',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                  }}
                ></WidgetNivo>
              </div>
              <div
                className="grid-cell"
                key="1"
                data-grid={{
                  w: 2,
                  h: 1,
                  x: 2,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Tokens"
                  secondaryText={shortenNumber(totalTokens, 4)}
                  classNames={{
                    root: 'h-full',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                  }}
                ></WidgetNivo>
              </div>
              <div
                key="2"
                data-grid={{
                  w: 2,
                  h: 1,
                  x: 4,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Flagged Tokens"
                  secondaryText={
                    tokensUnsupported
                      ? shortenNumber(tokensUnsupported, 2)
                      : '0'
                  }
                  classNames={{
                    root: 'h-full ',
                    header: 'p-2',
                    content:
                      'my-auto justify-center items-center overflow-y-hidden',
                  }}
                ></WidgetNivo>
              </div>
              <div
                key="3"
                data-grid={{
                  w: 2,
                  h: 1,
                  x: 6,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Programs"
                  secondaryText={totalPrograms}
                  classNames={{
                    root: 'h-full',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                  }}
                ></WidgetNivo>
              </div>
              <div
                key="4"
                data-grid={{
                  w: 2,
                  h: 1,
                  x: 8,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Unique Macros"
                  secondaryText={uniqueMacros}
                  classNames={{
                    root: 'h-full  ',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                  }}
                ></WidgetNivo>
              </div>
              <div
                className="grid-cell"
                key="5"
                data-grid={{
                  w: 2,
                  h: 1,
                  x: 10,
                  y: 0,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Custom Formats"
                  secondaryText={customFormatsUsed}
                  classNames={{
                    root: 'h-full ',
                    header: 'p-2',
                    content: 'my-auto justify-center items-center',
                  }}
                ></WidgetNivo>
              </div>
              <div
                className="grid-cell"
                key="6"
                data-grid={{
                  w: 4,
                  h: 2,
                  x: 0,
                  y: 1,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Tokens by Workload Type"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-64 place-content-center overflow-hidden">
                    <TreemapChart data={tokensByWorkloadTypes}></TreemapChart>
                  </div>
                </WidgetNivo>
              </div>

              <div
                key="7"
                data-grid={{
                  w: 4,
                  h: 2,
                  x: 4,
                  y: 1,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Programs by Workload Type"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-72 place-content-center">
                    <BarChart data={programsByWorkloadTypes} />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="8"
                data-grid={{
                  w: 4,
                  h: 2,
                  x: 8,
                  y: 1,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Unique Programs"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-60">
                    <PieChart
                      data={programUniqueness}
                      sortByValue={true}
                      enableArcLinkLabels={true}
                      enableArcLabels={false}
                      centeredMetric={`${codeUniqueness.toFixed(0)}%`}
                      legends={[
                        {
                          anchor: 'right',
                          direction: 'column',
                          justify: false,
                          translateX: 40,
                          translateY: 0,
                          itemsSpacing: 0,
                          itemWidth: 120,
                          itemHeight: 32,
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 12,
                          symbolShape: 'circle',
                        },
                      ]}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="9"
                data-grid={{
                  w: 6,
                  h: 2,
                  x: 0,
                  y: 3,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Program Size in Tokens"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-52 place-content-center overflow-hidden">
                    <Histogram
                      data={programBySizeInTokens}
                      isYAxisPercentage={true}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="10"
                data-grid={{
                  w: 6,
                  h: 2,
                  x: 6,
                  y: 3,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Cyclomatic Complexity"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-48 place-content-center">
                    <Histogram
                      data={cyclomaticComplexity}
                      legends={CC_CHART_LEGENDS}
                      isAxisDate={false}
                      isYAxisPercentage={true}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="11"
                data-grid={{
                  w: 4,
                  h: 2,
                  x: 0,
                  y: 5,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="SQL Tokens by Category"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-52 place-content-center">
                    <PieChart
                      data={SQLByCategory}
                      sortByValue={true}
                      enableArcLinkLabels={true}
                      enableArcLabels={false}
                      centeredMetric={String(totalSQL)}
                      legends={[
                        {
                          anchor: 'right',
                          direction: 'column',
                          justify: false,
                          translateX: 60,
                          translateY: 0,
                          itemsSpacing: 0,
                          itemWidth: 120,
                          itemHeight: 32,
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 12,
                          symbolShape: 'circle',
                        },
                      ]}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="12"
                data-grid={{
                  w: 4,
                  h: 2,
                  x: 4,
                  y: 5,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Statements by Type"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-52 place-content-center">
                    <PieChart
                      data={statementsByCategory}
                      sortByValue={true}
                      enableArcLinkLabels={true}
                      enableArcLabels={false}
                      centeredMetric={String(totalStatements)}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="13"
                data-grid={{
                  w: 4,
                  h: 2,
                  x: 8,
                  y: 5,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Data Steps by Class"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-52 place-content-center">
                    <PieChart
                      data={dataStepsByCategory}
                      sortByValue={true}
                      enableArcLinkLabels={true}
                      enableArcLabels={false}
                      centeredMetric={String(totalDataSteps)}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="21"
                data-grid={{
                  w: 3,
                  h: 2,
                  x: 0,
                  y: 7,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="SQL Pass-Through"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-60 place-content-center">
                    <PieChart
                      data={sqlPassThroughShare}
                      sortByValue={false}
                      enableArcLinkLabels={false}
                      enableArcLabels={false}
                      centeredMetric={`${sqlPassThroughPercentage.toFixed(0)}%`}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="20"
                data-grid={{
                  w: 3,
                  h: 2,
                  x: 3,
                  y: 7,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Filename Extensions"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-64 place-content-center">
                    <BarChart
                      data={top5fileNameExtensions}
                      sortByValue={false}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="19"
                data-grid={{
                  w: 3,
                  h: 2,
                  x: 6,
                  y: 7,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Filename Devices"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-64 place-content-center">
                    <BarChart data={top5fileNameDevices} sortByValue={false} />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="16"
                data-grid={{
                  w: 3,
                  h: 2,
                  x: 9,
                  y: 7,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Library Engines"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-64 place-content-center">
                    <BarChart data={libraryEngines} sortByValue={false} />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="14"
                data-grid={{
                  w: 3,
                  h: 2,
                  x: 0,
                  y: 9,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Top PROCs"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-60 place-content-center">
                    <ProgressBarChart data={top5Procs} sortByValue={false} />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="18"
                data-grid={{
                  w: 3,
                  h: 2,
                  x: 3,
                  y: 9,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Top Macros"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-64 place-content-center">
                    <ProgressBarChart data={topMacros} sortByValue={false} />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="17"
                data-grid={{
                  w: 3,
                  h: 2,
                  x: 6,
                  y: 9,
                  minW: 2,
                  minH: 2,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Top Functions"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-60 place-content-center">
                    <ProgressBarChart
                      data={top5Functions}
                      sortByValue={false}
                    />
                  </div>
                </WidgetNivo>
              </div>
              <div
                key="15"
                data-grid={{
                  w: 3,
                  h: 2,
                  x: 9,
                  y: 9,
                  minW: 1,
                  minH: 1,
                  static: !isResizable,
                }}
              >
                <WidgetNivo
                  title="Top Built-In Formats"
                  classNames={{
                    root: 'h-full',
                    header: 'text-center',
                    content: 'my-auto justify-center items-center',
                  }}
                >
                  <div className="h-60 place-content-center">
                    <ProgressBarChart
                      data={top5BuiltinFormats}
                      sortByValue={false}
                    />
                  </div>
                </WidgetNivo>
              </div>
            </ResponsiveGridLayout>
          </div>
        </>
      )}
    </div>
  );
};